import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  MenuItem,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { Stack } from "../mui";
import type { CSSProperties, ChangeEvent } from "react";
import { AIWrapper } from "./AiWrapper";

export type AiSearchFavorite = {
  id: number | string;
  name: string;
  query: string;
  searchContext?: string;
  isFavorite: boolean;
  useCount: number;
  createdAt?: string;
  createdDate?: string;
  updatedAt?: string;
  modifiedDate?: string;
  lastUsedAt?: string;
  lastUsedDate?: string;
  order?: number;
  displayOrder?: number;
};

type AiSearchFavoriteSort = "manual" | "mostUsed" | "recent" | "alpha";
type AiSearchFavoriteId = AiSearchFavorite["id"];
export type AiSearchFavoriteEdit = Partial<AiSearchFavorite> &
  Pick<AiSearchFavorite, "name" | "query" | "isFavorite">;

type AISearchHeaderProps = {
  value?: string;
  onChange?: (value: string) => void;
  onSubmit?: (value: string) => void | Promise<void>;
  validationErrors?: Record<string, string>;
  showFavorites?: boolean;
  userId?: number | string;
  completedSearch?: string;
  onFavoriteToast?: (message: string) => void;
  loadFavoriteSearches?: () => Promise<AiSearchFavorite[]>;
  createFavoriteSearch?: (favorite: AiSearchFavoriteEdit) => Promise<AiSearchFavorite>;
  updateFavoriteSearch?: (favorite: AiSearchFavoriteEdit) => Promise<AiSearchFavorite>;
  deleteFavoriteSearch?: (id: AiSearchFavoriteId) => Promise<void>;
  markFavoriteSearchUsed?: (id: AiSearchFavoriteId) => Promise<AiSearchFavorite>;
  saveFavoriteSearchOrder?: (favorites: { id: AiSearchFavoriteId; displayOrder: number }[]) => Promise<void>;
};

export const AISearchHeader = (props: AISearchHeaderProps) => {
  const [value, setValue] = useState(props.value ?? "");
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (props.value !== undefined) {
      setValue(props.value);
    }
  }, [props.value]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    props.onChange?.(e.target.value);
  };

  const submitSearch = async (query: string) => {
    if (isSearching) return;

    setIsSearching(true);

    try {
      await props.onSubmit?.(query);
    } finally {
      setTimeout(() => {
        setIsSearching(false);
      }, 900);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      submitSearch(value);
    }
  };

  const handleRun = async (query: string) => {
    setValue(query);
    props.onChange?.(query);
    await submitSearch(query);
  };

  return (
    <Stack direction="column" alignItems="center">
      <AIWrapper
        validationErrors={props.validationErrors}
        label="Navii Powered Search"
        trailing="Smart results tailored for you"
        isSearching={isSearching}
        maxWidth={900}
      >
        <input
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder="Enter Query..."
          style={aiSearchInputStyle}
        />
      </AIWrapper>
      {props.showFavorites && (
        <AiSearchFavorites
          currentQuery={value}
          completedSearch={props.completedSearch}
          userId={props.userId}
          onPopulate={(query) => {
            setValue(query);
            props.onChange?.(query);
          }}
          onRun={handleRun}
          onFavoriteToast={props.onFavoriteToast}
          loadFavoriteSearches={props.loadFavoriteSearches}
          createFavoriteSearch={props.createFavoriteSearch}
          updateFavoriteSearch={props.updateFavoriteSearch}
          deleteFavoriteSearch={props.deleteFavoriteSearch}
          markFavoriteSearchUsed={props.markFavoriteSearchUsed}
          saveFavoriteSearchOrder={props.saveFavoriteSearchOrder}
        />
      )}
    </Stack>
  );
}

const createFavoriteName = (query: string) => {
  const trimmed = query.trim();
  if (!trimmed) return "Untitled Search";
  return trimmed;
};

const getFavoriteDisplayName = (favorite: AiSearchFavorite) => {
  const name = favorite.name?.trim();
  const query = favorite.query?.trim();

  if (name?.endsWith("...") && query?.startsWith(name.slice(0, -3))) return query;

  return name || query || "Untitled Search";
};

const getFavoriteOrder = (favorite: AiSearchFavorite) => favorite.displayOrder ?? favorite.order ?? 0;
const getUpdatedDate = (favorite: AiSearchFavorite) => favorite.modifiedDate ?? favorite.updatedAt ?? favorite.createdDate ?? favorite.createdAt;
const getLastUsedDate = (favorite: AiSearchFavorite) => favorite.lastUsedDate ?? favorite.lastUsedAt ?? getUpdatedDate(favorite);

const sortFavorites = (favorites: AiSearchFavorite[], sort: AiSearchFavoriteSort) => {
  const sorted = [...favorites];

  if (sort === "mostUsed") return sorted.sort((a, b) => b.useCount - a.useCount || getFavoriteOrder(a) - getFavoriteOrder(b));
  if (sort === "recent") {
    return sorted.sort(
      (a, b) =>
        new Date(getLastUsedDate(b) ?? 0).getTime() - new Date(getLastUsedDate(a) ?? 0).getTime()
    );
  }
  if (sort === "alpha") return sorted.sort((a, b) => a.name.localeCompare(b.name));

  return sorted.sort((a, b) => getFavoriteOrder(a) - getFavoriteOrder(b) || b.useCount - a.useCount);
};

const upsertFavorite = (favorites: AiSearchFavorite[], nextFavorite: AiSearchFavorite) => {
  const existingIndex = favorites.findIndex((favorite) => favorite.id === nextFavorite.id);
  if (existingIndex < 0) return [...favorites, nextFavorite];

  return favorites.map((favorite) => (favorite.id === nextFavorite.id ? nextFavorite : favorite));
};

const showFavoriteError = (onFavoriteToast: ((message: string) => void) | undefined) => {
  onFavoriteToast?.("Favorite AI Searches are unavailable");
};

const AiSearchFavorites = (props: {
  currentQuery: string;
  completedSearch?: string;
  userId?: number | string;
  onPopulate: (query: string) => void;
  onRun: (query: string) => void;
  onFavoriteToast?: (message: string) => void;
  loadFavoriteSearches?: () => Promise<AiSearchFavorite[]>;
  createFavoriteSearch?: (favorite: AiSearchFavoriteEdit) => Promise<AiSearchFavorite>;
  updateFavoriteSearch?: (favorite: AiSearchFavoriteEdit) => Promise<AiSearchFavorite>;
  deleteFavoriteSearch?: (id: AiSearchFavoriteId) => Promise<void>;
  markFavoriteSearchUsed?: (id: AiSearchFavoriteId) => Promise<AiSearchFavorite>;
  saveFavoriteSearchOrder?: (favorites: { id: AiSearchFavoriteId; displayOrder: number }[]) => Promise<void>;
}) => {
  const [favorites, setFavorites] = useState<AiSearchFavorite[]>([]);
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [deletingFavorite, setDeletingFavorite] = useState<AiSearchFavorite | undefined>();
  const [renamingFavorite, setRenamingFavorite] = useState<AiSearchFavorite | undefined>();
  const [renameText, setRenameText] = useState("");
  const [searchText, setSearchText] = useState("");
  const [sort, setSort] = useState<AiSearchFavoriteSort>("manual");
  const [draggedId, setDraggedId] = useState<AiSearchFavoriteId | undefined>();

  useEffect(() => {
    let isMounted = true;
    props.loadFavoriteSearches?.()
      .then((loadedFavorites) => {
        if (isMounted) setFavorites(loadedFavorites ?? []);
      })
      .catch(() => {
        if (isMounted) {
          setFavorites([]);
          showFavoriteError(props.onFavoriteToast);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [props.loadFavoriteSearches, props.onFavoriteToast]);

  const orderedFavorites = useMemo(() => sortFavorites(favorites.filter((x) => x.isFavorite), "manual"), [favorites]);

  const visibleFavorites = orderedFavorites.slice(0, 3);
  const completedSearchIsFavorite = favorites.some(
    (favorite) => favorite.query.trim().toLowerCase() === props.completedSearch?.trim().toLowerCase()
  );

  const modalFavorites = useMemo(() => {
    const filtered = favorites.filter(
      (favorite) =>
        favorite.isFavorite &&
        (!searchText ||
          getFavoriteDisplayName(favorite).toLowerCase().includes(searchText.toLowerCase()) ||
          favorite.query.toLowerCase().includes(searchText.toLowerCase()))
    );
    return sortFavorites(filtered, sort);
  }, [favorites, searchText, sort]);

  const saveFavorite = async (query: string) => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return;

    const now = new Date().toISOString();
    const existing = favorites.find((favorite) => favorite.query.trim().toLowerCase() === trimmedQuery.toLowerCase());
    const favoriteToSave: AiSearchFavoriteEdit = existing
      ? { ...existing, isFavorite: true, updatedAt: now, displayOrder: existing.displayOrder ?? existing.order ?? favorites.length }
      : {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        name: createFavoriteName(trimmedQuery),
        query: trimmedQuery,
        isFavorite: true,
        createdAt: now,
        updatedAt: now,
        displayOrder: favorites.length,
      };

    try {
      if (existing && props.updateFavoriteSearch) {
        const savedFavorite = await props.updateFavoriteSearch(favoriteToSave);
        setFavorites((current) => upsertFavorite(current, savedFavorite));
      } else if (props.createFavoriteSearch) {
        const savedFavorite = await props.createFavoriteSearch(favoriteToSave);
        setFavorites((current) => upsertFavorite(current, savedFavorite));
      } else {
        setFavorites((current) => upsertFavorite(current, favoriteToSave as AiSearchFavorite));
      }
    } catch {
      showFavoriteError(props.onFavoriteToast);
      return;
    }

    props.onFavoriteToast?.("Added to Favorites");
  };

  const runFavorite = async (favorite: AiSearchFavorite) => {
    const now = new Date().toISOString();
    setFavorites((current) =>
      current.map((item) =>
        item.id === favorite.id ? { ...item, useCount: item.useCount + 1, lastUsedAt: now, updatedAt: now } : item
      )
    );
    if (props.markFavoriteSearchUsed) {
      try {
        const savedFavorite = await props.markFavoriteSearchUsed(favorite.id);
        setFavorites((current) => upsertFavorite(current, savedFavorite));
      } catch {
        showFavoriteError(props.onFavoriteToast);
      }
    }
    props.onRun(favorite.query);
  };

  const updateFavorite = async (id: AiSearchFavoriteId, updater: (favorite: AiSearchFavorite) => AiSearchFavorite) => {
    const existing = favorites.find((favorite) => favorite.id === id);
    if (!existing) return;

    const updatedFavorite = updater(existing);
    setFavorites((current) => current.map((favorite) => (favorite.id === id ? updatedFavorite : favorite)));

    if (props.updateFavoriteSearch) {
      try {
        const savedFavorite = await props.updateFavoriteSearch(updatedFavorite);
        setFavorites((current) => upsertFavorite(current, savedFavorite));
      } catch {
        setFavorites((current) => current.map((favorite) => (favorite.id === id ? existing : favorite)));
        showFavoriteError(props.onFavoriteToast);
      }
    }
  };

  const deleteFavorite = async (id: AiSearchFavoriteId) => {
    if (props.deleteFavoriteSearch) {
      try {
        await props.deleteFavoriteSearch(id);
      } catch {
        showFavoriteError(props.onFavoriteToast);
        return;
      }
    }
    setFavorites((current) => current.filter((favorite) => favorite.id !== id));
    setDeletingFavorite(undefined);
  };

  const openRenameFavorite = (favoriteToRename: AiSearchFavorite) => {
    setRenamingFavorite(favoriteToRename);
    setRenameText(getFavoriteDisplayName(favoriteToRename));
  };

  const closeRenameFavorite = () => {
    setRenamingFavorite(undefined);
    setRenameText("");
  };

  const saveRenameFavorite = () => {
    if (!renamingFavorite) return;

    const trimmedName = renameText.trim();
    if (!trimmedName) return;

    updateFavorite(renamingFavorite.id, (favorite) => ({
      ...favorite,
      name: trimmedName,
      query: trimmedName,
      updatedAt: new Date().toISOString(),
    }));
    closeRenameFavorite();
  };

  const reorderFavorite = (targetId: AiSearchFavoriteId) => {
    if (!draggedId || draggedId === targetId) return;

    let reorderedFavorites: AiSearchFavorite[] = [];
    setFavorites((current) => {
      const manualFavorites = sortFavorites(current.filter((favorite) => favorite.isFavorite), "manual");
      const draggedIndex = manualFavorites.findIndex((favorite) => favorite.id === draggedId);
      const targetIndex = manualFavorites.findIndex((favorite) => favorite.id === targetId);
      if (draggedIndex < 0 || targetIndex < 0) return current;

      const reordered = [...manualFavorites];
      const [dragged] = reordered.splice(draggedIndex, 1);
      reordered.splice(targetIndex, 0, dragged);

      reorderedFavorites = reordered.map((favorite, index) => ({ ...favorite, displayOrder: index, order: index }));

      return current.map((favorite) => {
        const nextIndex = reordered.findIndex((item) => item.id === favorite.id);
        return nextIndex >= 0 ? { ...favorite, displayOrder: nextIndex, order: nextIndex, updatedAt: new Date().toISOString() } : favorite;
      });
    });

    props.saveFavoriteSearchOrder?.(
      reorderedFavorites.map((favorite) => ({ id: favorite.id, displayOrder: favorite.displayOrder ?? 0 }))
    ).catch(() => showFavoriteError(props.onFavoriteToast));
  };

  const emptyState = orderedFavorites.length === 0;

  return (
    <div style={favoriteStyles.wrapper}>
      <div style={favoriteStyles.header}>
        <div style={favoriteStyles.headerTitle}>
          <StarIcon sx={{ color: "#F4B400", fontSize: 18 }} />
          <span>Favorite Navii Searches ({orderedFavorites.length})</span>
        </div>
        {orderedFavorites.length > 0 && (
          <button type="button" style={favoriteStyles.viewAllButton} onClick={() => setViewAllOpen(true)}>
            View All &gt;
          </button>
        )}
      </div>

      {emptyState ? (
        <div style={favoriteStyles.emptyState}>
          <StarBorderIcon sx={{ color: "#F4B400", fontSize: 24 }} />
          <div>
            <div style={favoriteStyles.emptyTitle}>Favorite Navii Searches</div>
            <div style={favoriteStyles.emptyCopy}>Save frequently used Navii searches for quick access.</div>
          </div>
          <Button
            size="small"
            variant="outlined"
            disabled={!props.currentQuery.trim()}
            onClick={() => saveFavorite(props.currentQuery)}
            sx={{ borderColor: "#0057B8", color: "#0057B8", textTransform: "none" }}
          >
            Create First Favorite
          </Button>
        </div>
      ) : (
        <div style={favoriteStyles.rowStack}>
          {visibleFavorites.map((favorite) => (
            <FavoriteRow
              key={favorite.id}
              favorite={favorite}
              onPopulate={props.onPopulate}
              onRun={runFavorite}
              onRename={() => openRenameFavorite(favorite)}
              onToggleFavorite={() => updateFavorite(favorite.id, (item) => ({ ...item, isFavorite: !item.isFavorite }))}
              onDelete={() => setDeletingFavorite(favorite)}
            />
          ))}
        </div>
      )}

      {props.completedSearch?.trim() && !completedSearchIsFavorite && (
        <div style={favoriteStyles.completeBar}>
          <span>Search Complete</span>
          <button type="button" style={favoriteStyles.addFavoriteButton} onClick={() => saveFavorite(props.completedSearch!)}>
            <StarBorderIcon sx={{ fontSize: 16 }} />
            Add to Favorites
          </button>
        </div>
      )}

      <Dialog open={Boolean(deletingFavorite)} onClose={() => setDeletingFavorite(undefined)} maxWidth="xs" fullWidth>
        <DialogTitle sx={favoriteStyles.confirmTitle}>Delete favorite?</DialogTitle>
        <DialogContent sx={{ color: "#4B5563", fontSize: 14 }}>
          This will remove "{deletingFavorite ? getFavoriteDisplayName(deletingFavorite) : ""}" from your saved AI searches.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingFavorite(undefined)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            color="error"
            onClick={() => deletingFavorite && deleteFavorite(deletingFavorite.id)}
            sx={{ textTransform: "none" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(renamingFavorite)}
        onClose={closeRenameFavorite}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, top: "-12vh" } }}
      >
        <DialogTitle sx={favoriteStyles.renameTitle}>
          <div style={favoriteStyles.renameHeaderContent}>
            <FavoriteWheelIcon color="#FFFFFF" size={34} />
            <div>
              <div style={favoriteStyles.renameHeaderText}>Rename favorite</div>
              <div style={favoriteStyles.renameHeaderSubtext}>Give your favorite search a new name.</div>
            </div>
          </div>
          <IconButton size="small" onClick={closeRenameFavorite} sx={favoriteStyles.renameCloseButton}>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 0, pb: 2 }}>
          <div style={favoriteStyles.renameFieldWrap}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Favorite name"
              value={renameText}
              onChange={(event) => setRenameText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  saveRenameFavorite();
                }
              }}
              InputProps={{
                endAdornment: renameText ? (
                  <IconButton size="small" onClick={() => setRenameText("")} sx={{ color: "#6B7280" }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ) : undefined,
              }}
              sx={{
                "& .MuiInputLabel-root": { color: "#6B7280", fontSize: 13, fontWeight: 700 },
                "& .MuiInputLabel-root.Mui-focused": { color: "#0057B8" },
                "& .MuiOutlinedInput-root": { borderRadius: 2 },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#0057B8" },
              }}
            />
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
          <Button
            onClick={closeRenameFavorite}
            sx={favoriteStyles.renameCancelButton}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!renameText.trim()}
            onClick={saveRenameFavorite}
            sx={favoriteStyles.renameSaveButton}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={viewAllOpen}
        onClose={() => setViewAllOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { width: "min(900px, calc(100vw - 32px))", borderRadius: 4 } }}
      >
        <DialogTitle sx={favoriteStyles.modalTitle}>
          <div style={favoriteStyles.modalHeaderContent}>
            <FavoriteWheelIcon color="#FFFFFF" size={34} />
            <span>Favorite Navii Searches</span>
          </div>
          <Button onClick={() => setViewAllOpen(false)} sx={favoriteStyles.modalCloseButton}>
            Close
          </Button>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <div style={favoriteStyles.modalControls}>
            <TextField
              size="small"
              label="Search Favorites..."
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              fullWidth
            />
            <TextField
              size="small"
              label="Sort by"
              select
              value={sort}
              onChange={(event) => setSort(event.target.value as AiSearchFavoriteSort)}
              sx={{ minWidth: 170 }}
            >
              <MenuItem value="mostUsed">Most Used</MenuItem>
              <MenuItem value="recent">Recently Used</MenuItem>
              <MenuItem value="alpha">Alphabetical</MenuItem>
              <MenuItem value="manual">Manually Ordered</MenuItem>
            </TextField>
          </div>

          <div style={favoriteStyles.modalList}>
            {modalFavorites.map((favorite) => (
              <div
                className="ai-search-favorite-modal-row"
                key={favorite.id}
                draggable={sort === "manual"}
                onDragStart={() => setDraggedId(favorite.id)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={() => reorderFavorite(favorite.id)}
                style={favoriteStyles.modalRow}

              >
                <button type="button" style={favoriteStyles.modalRowText} onClick={() => props.onPopulate(favorite.query)}>
                  <FavoriteWheelIcon />
                  <span style={favoriteStyles.modalRowTitle}>{getFavoriteDisplayName(favorite)}</span>
                </button>
                <div style={favoriteStyles.modalRowActions}>
                  <Tooltip title="Rename">
                    <IconButton size="small" onClick={() => openRenameFavorite(favorite)} sx={favoriteStyles.blueIconButton}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => setDeletingFavorite(favorite)} sx={favoriteStyles.deleteIconButton}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <div style={favoriteStyles.actionDivider} />
                  <Tooltip title="Run Search">
                    <IconButton size="small" onClick={() => runFavorite(favorite)} sx={favoriteStyles.runIconButton}>
                      <PlayArrowIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
        <DialogActions>
          {/* <Button onClick={() => setViewAllOpen(false)} sx={{ textTransform: "none" }}>
            Close
          </Button> */}
        </DialogActions>
      </Dialog>
    </div>
  );
};

const FavoriteRow = (props: {
  favorite: AiSearchFavorite;
  onPopulate: (query: string) => void;
  onRun: (favorite: AiSearchFavorite) => void;
  onRename: () => void;
  onToggleFavorite: () => void;
  onDelete: () => void;
}) => {
  return (
    <div className="ai-search-favorite-row" style={favoriteStyles.row}>
      <button type="button" style={favoriteStyles.rowBody} onClick={() => props.onPopulate(props.favorite.query)}>
        <FavoriteWheelIcon />
        <span style={favoriteStyles.rowTitle}>{getFavoriteDisplayName(props.favorite)}</span>
      </button>
      <div style={favoriteStyles.rowActions}>
        <Tooltip title="Rename">
          <IconButton size="small" onClick={props.onRename} sx={favoriteStyles.blueIconButton}>
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {/* <Tooltip title={props.favorite.isFavorite ? "Unfavorite" : "Favorite"}>
          <IconButton size="small" onClick={props.onToggleFavorite}>
            {props.favorite.isFavorite ? (
              <StarIcon sx={{ color: "#F4B400", fontSize: 20 }} />
            ) : (
              <StarBorderIcon sx={{ color: "#F4B400", fontSize: 20 }} />
            )}
          </IconButton>
        </Tooltip> */}
        <Tooltip title="Delete">
          <IconButton size="small" onClick={props.onDelete} sx={favoriteStyles.deleteIconButton}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <div style={favoriteStyles.actionDivider} />
        <Tooltip title="Run Search">
          <IconButton size="small" sx={favoriteStyles.runIconButton} onClick={() => props.onRun(props.favorite)}>
            <PlayArrowIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
};

const FavoriteWheelIcon = (props: { color?: string; size?: number }) => {
  const color = props.color ?? "#0057B8";
  const size = props.size ?? 20;

  return (
    <svg
      className="ai-search-favorite-wheel"
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      aria-hidden="true"
    >
      <g className="ai-search-favorite-wheel-spin">
        <circle cx="32" cy="32" r="18" stroke={color} strokeWidth="5" />
        <circle cx="32" cy="32" r="7" stroke={color} strokeWidth="5" />
        <path d="M32 11V23" stroke={color} strokeWidth="4" strokeLinecap="round" />
        <path d="M32 41V53" stroke={color} strokeWidth="4" strokeLinecap="round" />
        <path d="M11 32H23" stroke={color} strokeWidth="4" strokeLinecap="round" />
        <path d="M41 32H53" stroke={color} strokeWidth="4" strokeLinecap="round" />
        <path d="M17.2 17.2L25.7 25.7" stroke={color} strokeWidth="4" strokeLinecap="round" />
        <path d="M38.3 38.3L46.8 46.8" stroke={color} strokeWidth="4" strokeLinecap="round" />
        <path d="M46.8 17.2L38.3 25.7" stroke={color} strokeWidth="4" strokeLinecap="round" />
        <path d="M25.7 38.3L17.2 46.8" stroke={color} strokeWidth="4" strokeLinecap="round" />
        <circle cx="32" cy="8.5" r="3.5" fill={color} />
        <circle cx="32" cy="55.5" r="3.5" fill={color} />
        <circle cx="8.5" cy="32" r="3.5" fill={color} />
        <circle cx="55.5" cy="32" r="3.5" fill={color} />
        <circle cx="15.4" cy="15.4" r="3.2" fill={color} />
        <circle cx="48.6" cy="48.6" r="3.2" fill={color} />
        <circle cx="48.6" cy="15.4" r="3.2" fill={color} />
        <circle cx="15.4" cy="48.6" r="3.2" fill={color} />
        <circle cx="32" cy="32" r="3" fill={color} />
      </g>
    </svg>
  );
};

const aiSearchInputStyle: CSSProperties = {
  border: "none",
  outline: "none",
  background: "transparent",
  fontSize: 14,
  fontWeight: 500,
  color: "#111827",
  width: "100%",
  minWidth: 0,
};

const favoriteStyles: Record<string, CSSProperties> = {
  wrapper: {
    width: "min(100%, 900px)",
    minWidth: 0,
    boxSizing: "border-box",
    marginTop: -4,
    marginBottom: 16,
    padding: "14px 16px",
    border: "1px solid #E5E7EB",
    borderRadius: 10,
    background: "#FFFFFF",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 12,
  },
  headerTitle: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    color: "#111827",
    fontSize: 16,
    fontWeight: 600,
  },
  viewAllButton: {
    border: 0,
    background: "transparent",
    color: "#0057B8",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
    padding: 0,
    whiteSpace: "nowrap",
  },
  rowStack: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  row: {
    boxSizing: "border-box",
    width: "100%",
    minHeight: 56,
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    alignItems: "flex-start",
    gap: 8,
    border: "1px solid #E5E7EB",
    borderRadius: 9,
    background: "#FFFFFF",
    padding: "8px 8px 8px 12px",
    transition: "background 140ms ease, border-color 140ms ease",
  },
  rowBody: {
    minWidth: 0,
    width: "100%",
    boxSizing: "border-box",
    alignSelf: "stretch",
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    border: 0,
    background: "transparent",
    cursor: "pointer",
    padding: 0,
    textAlign: "left",
    paddingTop: 8,
  },
  rowTitle: {
    color: "#111827",
    display: "block",
    minWidth: 0,
    width: "100%",
    maxWidth: "100%",
    fontSize: 15,
    fontWeight: 500,
    lineHeight: 1.3,
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    whiteSpace: "normal",
  },
  rowActions: {
    width: 148,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 1,
    paddingTop: 4,
  },
  blueIconButton: {
    color: "#0057B8",
  },
  deleteIconButton: {
    color: "#DC2626",
  },
  actionDivider: {
    width: 1,
    height: 24,
    background: "#E5E7EB",
    margin: "0 3px",
  },
  runIconButton: {
    color: "#0057B8",
    border: "1px solid #BFDBFE",
    backgroundColor: "#EFF6FF",
    height: 32,
    width: 32,
  },
  emptyState: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    border: "1px dashed #E5E7EB",
    borderRadius: 9,
    background: "#F8FAFC",
    padding: "10px 12px",
    flexWrap: "wrap",
  },
  emptyTitle: {
    color: "#111827",
    fontSize: 14,
    fontWeight: 600,
  },
  emptyCopy: {
    color: "#6B7280",
    fontSize: 13,
  },
  completeBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginTop: 12,
    padding: "10px 12px",
    borderRadius: 8,
    background: "#F8FAFC",
    color: "#374151",
    fontSize: 13,
    fontWeight: 600,
  },
  addFavoriteButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    border: 0,
    background: "transparent",
    color: "#0057B8",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 700,
    padding: 0,
  },
  modalTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    backgroundColor: "#0057B8",
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: 800,
    padding: "18px 22px",
  },
  modalHeaderContent: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
  },
  modalCloseButton: {
    border: "1px solid rgba(255,255,255,0.75)",
    borderRadius: "4px",
    color: "#FFFFFF",
    flexShrink: 0,
    fontWeight: 700,
    fontSize: 12,
    minWidth: 72,
    padding: "5px 16px",
    textTransform: "uppercase",
  },
  confirmTitle: {
    fontSize: 18,
    fontWeight: 700,
  },
  renameTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    backgroundColor: "#0057B8",
    color: "#FFFFFF",
    padding: "18px 22px",
  },
  renameHeaderContent: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
  },
  renameHeaderText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: 800,
    lineHeight: 1.1,
  },
  renameHeaderSubtext: {
    color: "rgba(255,255,255,0.88)",
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1.35,
    marginTop: 3,
  },
  renameCloseButton: {
    color: "#FFFFFF",
    flexShrink: 0,
  },
  renameFieldWrap: {
    marginTop: 27,
  },
  renameCancelButton: {
    width: 82,
    minWidth: 82,
    border: "1px solid #0057B8",
    borderRadius: "4px",
    backgroundColor: "#FFFFFF",
    color: "#0057B8",
    fontSize: 12,
    fontWeight: 700,
    padding: "5px 16px",
    textTransform: "uppercase",
  },
  renameSaveButton: {
    width: 82,
    minWidth: 82,
    borderRadius: "4px",
    backgroundColor: "#0057B8",
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: 700,
    padding: "5px 16px",
    textTransform: "uppercase",
  },
  modalControls: {
    display: "flex",
    gap: 12,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  modalList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    width: "100%",
  },
  modalRow: {
    boxSizing: "border-box",
    width: "100%",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    alignItems: "flex-start",
    gap: 6,
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    background: "#FFFFFF",
    padding: "8px",
    transition: "background 140ms ease, border-color 140ms ease",
  },
  modalRowText: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    minWidth: 0,
    width: "100%",
    boxSizing: "border-box",
    border: 0,
    background: "transparent",
    color: "#111827",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 500,
    overflow: "visible",
    padding: "7px 0",
    textAlign: "left",
  },
  modalRowTitle: {
    display: "block",
    flex: "1 1 auto",
    minWidth: 0,
    width: "100%",
    lineHeight: 1.35,
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    whiteSpace: "normal",
  },
  modalRowActions: {
    width: 112,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 1,
    paddingTop: 2,
  },

};

if (typeof document !== "undefined") {
  const styleId = "ai-search-favorites-styles";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.innerHTML = `
      .ai-search-favorite-row:hover {
        background: #F8FAFC;
        border-color: #D1D5DB;
      }
      .ai-search-favorite-wheel {
        flex-shrink: 0;
        margin-top: 1px;
      }
      .ai-search-favorite-wheel-spin {
        transform-box: fill-box;
        transform-origin: center;
        transition: transform 420ms cubic-bezier(.2,.8,.2,1);
      }
      .ai-search-favorite-row:hover .ai-search-favorite-wheel-spin,
      .ai-search-favorite-modal-row:hover .ai-search-favorite-wheel-spin {
        transform: rotate(180deg);
      }
      .ai-search-favorite-modal-row:hover {
        background: #F8FAFC;
        border-color: #D1D5DB;
      }
    `;
    document.head.appendChild(style);
  }
}
